import { findMatchingKey } from '../utils/quizMatching';

export type QuizEditorQuestion = {
  question: string;
  options: string[];
  correctIndexes: number[];
};

export type QuizEditorLink = {
  id: string;
  title: string;
  route: string;
  module: string;
};

const OVERRIDES_KEY = 'quiz_editor_overrides_v1';
const CUSTOM_LIST_KEY = 'quiz_editor_custom_list_v1';

const isBrowser = typeof window !== 'undefined';

export const loadQuizOverrides = (): Record<string, QuizEditorQuestion[]> => {
  if (!isBrowser) return {};
  try {
    const raw = window.localStorage.getItem(OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const saveQuizOverrides = (map: Record<string, QuizEditorQuestion[]>) => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(OVERRIDES_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
};

export const getOverrideQuestions = (candidates: string[]): { key: string; questions: QuizEditorQuestion[] } | null => {
  const overrides = loadQuizOverrides();
  const keys = Object.keys(overrides);
  const match = findMatchingKey(keys, candidates);
  if (!match) return null;
  return { key: match, questions: overrides[match] || [] };
};

export const setOverrideQuestions = (key: string, questions: QuizEditorQuestion[]) => {
  if (!key) return;
  const overrides = loadQuizOverrides();
  overrides[key] = questions;
  saveQuizOverrides(overrides);
};

export const removeOverrideQuestions = (key: string) => {
  if (!key) return;
  const overrides = loadQuizOverrides();
  if (overrides[key]) {
    delete overrides[key];
    saveQuizOverrides(overrides);
  }
};

export const loadCustomQuizLinks = (): QuizEditorLink[] => {
  if (!isBrowser) return [];
  try {
    const raw = window.localStorage.getItem(CUSTOM_LIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveCustomQuizLinks = (list: QuizEditorLink[]) => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(CUSTOM_LIST_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
};

export const upsertCustomQuizLink = (link: QuizEditorLink) => {
  if (!link?.id) return;
  const list = loadCustomQuizLinks();
  const index = list.findIndex((item) => item.id.toUpperCase() === link.id.toUpperCase());
  if (index >= 0) {
    list[index] = link;
  } else {
    list.push(link);
  }
  saveCustomQuizLinks(list);
};

export const removeCustomQuizLink = (id: string) => {
  if (!id) return;
  const list = loadCustomQuizLinks().filter((item) => item.id.toUpperCase() !== id.toUpperCase());
  saveCustomQuizLinks(list);
};
